<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ChatAPI from 'custom/api/chat';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { useChat } from './useChat';
import { useAgents } from './useAgents';
import {
  notificationsEnabled,
  notificationsSupported,
  requestNotifications,
} from './notifications';
import PeoplePicker from './PeoplePicker.vue';

// Team chat home: my channels and DMs, channels to join, new channel / new DM, search across everything
const { t, locale } = useLocoI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const agents = useAgents();
const {
  joinedChannels,
  directChannels,
  joinableChannels,
  upsertChannel,
  reload,
} = useChat({
  currentUserId: currentUser.value?.id,
});

const channelDialogRef = ref(null);
const directDialogRef = ref(null);
const newChannel = ref({
  name: '',
  description: '',
  private: false,
  members: [],
});
const directPeople = ref([]);
const query = ref('');
const results = ref(null);
const notificationsOn = ref(notificationsEnabled());
let searchTimer = null;

const otherAgents = computed(() =>
  agents.value.filter(agent => agent.id !== currentUser.value?.id)
);

const open = channel =>
  router.push({
    name: 'loco_team_chat_channel',
    params: {
      accountId: route.params.accountId,
      channelId: channel.id || channel.channel_id,
    },
  });

const fail = error =>
  useAlert(
    t('LOCO.CHAT.ERROR', {
      error: error.response?.data?.error || error.message,
    })
  );

const createChannel = async () => {
  try {
    const { data } = await ChatAPI.createChannel({
      name: newChannel.value.name,
      description: newChannel.value.description,
      kind: newChannel.value.private ? 'private' : 'public',
      member_ids: newChannel.value.members,
    });
    upsertChannel(data);
    channelDialogRef.value.close();
    newChannel.value = {
      name: '',
      description: '',
      private: false,
      members: [],
    };
    open(data);
  } catch (error) {
    fail(error);
  }
};

const startDirect = async () => {
  if (!directPeople.value.length) return;
  try {
    const { data } = await ChatAPI.direct(directPeople.value);
    upsertChannel(data);
    directDialogRef.value.close();
    directPeople.value = [];
    open(data);
  } catch (error) {
    fail(error);
  }
};

const join = async channel => {
  try {
    const { data } = await ChatAPI.join(channel.id);
    upsertChannel(data);
    open(data);
  } catch (error) {
    fail(error);
  }
};

const search = () => {
  clearTimeout(searchTimer);
  if (query.value.trim().length < 2) {
    results.value = null;
    return;
  }
  searchTimer = setTimeout(async () => {
    const { data } = await ChatAPI.search(query.value.trim());
    results.value = data.payload;
  }, 300);
};

const enableNotifications = async () => {
  notificationsOn.value = (await requestNotifications()) === 'granted';
};

reload().catch(() => {});

const when = timestamp =>
  new Date(timestamp * 1000).toLocaleString(locale.value.replace('_', '-'), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-8">
      <header class="flex flex-wrap items-start gap-4">
        <div class="flex flex-col gap-1 flex-1 min-w-0">
          <h1 class="text-heading-1 text-n-slate-12">
            {{ t('LOCO.CHAT.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">{{ t('LOCO.CHAT.SUBTITLE') }}</p>
        </div>
        <div class="flex gap-2">
          <Button
            sm
            faded
            slate
            icon="i-lucide-message-square-plus"
            :label="t('LOCO.CHAT.NEW_DIRECT')"
            @click="directDialogRef.open()"
          />
          <Button
            sm
            icon="i-lucide-hash"
            :label="t('LOCO.CHAT.NEW_CHANNEL')"
            @click="channelDialogRef.open()"
          />
        </div>
      </header>

      <button
        v-if="notificationsSupported() && !notificationsOn"
        type="button"
        class="self-start flex items-center gap-2 text-sm text-n-blue-11 hover:underline"
        @click="enableNotifications"
      >
        <Icon icon="i-lucide-bell" class="size-4" />
        {{ t('LOCO.CHAT.ENABLE_NOTIFICATIONS') }}
      </button>

      <div class="flex flex-col gap-3">
        <div class="relative">
          <Icon
            icon="i-lucide-search"
            class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10"
          />
          <input
            v-model="query"
            type="search"
            class="!mb-0 !pl-9"
            :placeholder="t('LOCO.CHAT.SEARCH_PLACEHOLDER')"
            @input="search"
          />
        </div>
        <ul
          v-if="results"
          class="flex flex-col rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <li v-if="!results.length" class="px-4 py-3 text-sm text-n-slate-11">
            {{ t('LOCO.CHAT.SEARCH_EMPTY') }}
          </li>
          <li
            v-for="result in results"
            :key="result.id"
            class="flex flex-col gap-0.5 px-4 py-2 cursor-pointer hover:bg-n-alpha-1"
            @click="open(result)"
          >
            <span class="text-xs text-n-slate-11">
              {{ result.sender?.name }} ·
              {{ t('LOCO.CHAT.IN_CHANNEL', { name: result.channel_name }) }} ·
              {{ when(result.created_at) }}
            </span>
            <span class="text-sm text-n-slate-12 truncate">{{
              result.content
            }}</span>
          </li>
        </ul>
      </div>

      <section
        v-for="group in [
          { title: t('LOCO.CHAT.MY_CHANNELS'), items: joinedChannels },
          { title: t('LOCO.CHAT.DIRECT_MESSAGES'), items: directChannels },
        ]"
        :key="group.title"
        class="flex flex-col gap-2"
      >
        <h2 class="text-heading-3 text-n-slate-12">{{ group.title }}</h2>
        <p v-if="!group.items.length" class="text-sm text-n-slate-11">
          {{ t('LOCO.CHAT.NO_CHANNELS') }}
        </p>
        <ul class="flex flex-col">
          <li
            v-for="channel in group.items"
            :key="channel.id"
            class="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
            @click="open(channel)"
          >
            <Icon
              :icon="
                channel.kind === 'direct'
                  ? 'i-lucide-user'
                  : channel.kind === 'private'
                    ? 'i-lucide-lock'
                    : 'i-lucide-hash'
              "
              class="size-4 text-n-slate-11"
            />
            <span
              class="flex-1 text-sm text-n-slate-12 truncate"
              :class="{ 'font-semibold': channel.unread_count }"
            >
              {{ channel.name }}
            </span>
            <span
              v-if="channel.mention_count"
              class="text-xs font-semibold text-n-ruby-11"
            >
              {{ `@${channel.mention_count}` }}
            </span>
            <span
              v-if="channel.unread_count"
              class="min-w-5 h-5 px-1.5 rounded-full bg-n-brand text-white text-xs leading-5 text-center"
            >
              {{ channel.unread_count }}
            </span>
          </li>
        </ul>
      </section>

      <section v-if="joinableChannels.length" class="flex flex-col gap-2">
        <h2 class="text-heading-3 text-n-slate-12">
          {{ t('LOCO.CHAT.JOINABLE') }}
        </h2>
        <ul class="flex flex-col">
          <li
            v-for="channel in joinableChannels"
            :key="channel.id"
            class="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-n-alpha-1"
          >
            <Icon icon="i-lucide-hash" class="size-4 text-n-slate-11" />
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm text-n-slate-12">{{ channel.name }}</span>
              <span class="text-xs text-n-slate-11 truncate">
                {{
                  channel.description ||
                  t(
                    'LOCO.CHAT.MEMBERS',
                    { count: channel.member_count },
                    channel.member_count
                  )
                }}
              </span>
            </div>
            <Button
              xs
              faded
              slate
              :label="t('LOCO.CHAT.OPEN')"
              @click="open(channel)"
            />
            <Button xs :label="t('LOCO.CHAT.JOIN')" @click="join(channel)" />
          </li>
        </ul>
      </section>
    </div>

    <Dialog
      ref="channelDialogRef"
      :title="t('LOCO.CHAT.NEW_CHANNEL')"
      :confirm-button-label="t('LOCO.CHAT.CREATE')"
      :cancel-button-label="t('LOCO.CHAT.CANCEL')"
      :disable-confirm-button="!newChannel.name.trim()"
      @confirm="createChannel"
    >
      <div class="flex flex-col gap-3 text-sm">
        <label class="flex flex-col gap-1">
          <span class="font-medium text-n-slate-12">{{
            t('LOCO.CHAT.CHANNEL_NAME')
          }}</span>
          <input
            v-model="newChannel.name"
            type="text"
            class="!mb-0"
            :placeholder="t('LOCO.CHAT.CHANNEL_NAME_PLACEHOLDER')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="font-medium text-n-slate-12">{{
            t('LOCO.CHAT.CHANNEL_DESCRIPTION')
          }}</span>
          <input v-model="newChannel.description" type="text" class="!mb-0" />
        </label>
        <label class="flex items-center gap-2">
          <input v-model="newChannel.private" type="checkbox" />
          <span>{{ t('LOCO.CHAT.CHANNEL_PRIVATE') }}</span>
        </label>
        <span class="font-medium text-n-slate-12">{{
          t('LOCO.CHAT.CHANNEL_MEMBERS')
        }}</span>
        <PeoplePicker
          v-model="newChannel.members"
          :people="otherAgents"
          :placeholder="t('LOCO.CHAT.PICK_PEOPLE')"
        />
      </div>
    </Dialog>

    <Dialog
      ref="directDialogRef"
      :title="t('LOCO.CHAT.NEW_DIRECT')"
      :confirm-button-label="t('LOCO.CHAT.START')"
      :cancel-button-label="t('LOCO.CHAT.CANCEL')"
      :disable-confirm-button="!directPeople.length"
      @confirm="startDirect"
    >
      <PeoplePicker
        v-model="directPeople"
        :people="otherAgents"
        :placeholder="t('LOCO.CHAT.PICK_PEOPLE')"
      />
    </Dialog>
  </div>
</template>
